import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';

import {
    CompNavItem,
    HomeAwayNavItem,
    OtherMatchesToggle,
    ResponsiveHomeAwayNavItem,
    CalendarButton
} from 'common/react/components/competition-nav/nav-items';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { ResponsiveSeasonNavItem } from 'common/react/components/competition-nav/nav-items';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { FilterButton } from 'common/react/components/FilterButton';
import { ResetFilters } from 'widgets/statspro/js/components/common/stats-section/StatsSectionNav/ResetFilters';

export const FixturesNav = ({
    competitionId,
    seasonId,
    competitionProviderId,
    seasonPid,
    ecalId,
    title
}) => {
    const windowSize = useWindowSize();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);

    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const initialValues = {
        competitionId,
        seasonId,
        competitionProviderId,
        seasonPid
    };

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);

    useNavSync({ ...options, initialValues });

    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title,
        filtersState: nav,
        options,
        filtersFields: ['competition', 'season', 'homeaway', 'otherMatches'],
        filtersRef: competitionNavRef
    });

    const renderDesktop = (
        <>
            <StatsSection.ScrollableItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    hideLabel={false}
                />
                <FilterDropdown
                    title={title}
                    icon="season-trophy"
                    navItem="season"
                    items={seasons}
                />
                <HomeAwayNavItem title={title} />
                <ResetFilters classes={'u-margin-right-auto'} />
            </StatsSection.ScrollableItems>
            <StatsSection.NavItems>
                <OtherMatchesToggle classes={'no-border'} />
                {ecalId && (
                    <CalendarButton classes="calendar-button" ecalId={ecalId} />
                )}
            </StatsSection.NavItems>
        </>
    );

    const renderMobile = (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    hideLabel={true}
                />
                <FilterButton
                    classes="fixtures-nav__filter-button"
                    onChange={() => setAreFilterOptionsVisible((prev) => !prev)}
                    areFilterOptionsVisible={areFilterOptionsVisible}
                    showFilterLabel={false}
                    showActiveCircle
                />
                {ecalId && (
                    <CalendarButton
                        classes="fixtures-nav__calendar-button stats-leaders-nav__button no-border"
                        ecalId={ecalId}
                    />
                )}
                <OtherMatchesToggle
                    classes="fixtures-nav__other-matches-toggle"
                    labelRightPosition
                />
            </StatsSection.NavItems>
            {currentFilters.competitionId &&
                currentFilters.compSeason?.id &&
                areFilterOptionsVisible && (
                    <StatsSection.NavFilterItems>
                        <ResponsiveSeasonNavItem
                            seasons={seasons}
                            title={title}
                            hideLabel
                        />
                        <ResponsiveHomeAwayNavItem title={title} />
                    </StatsSection.NavFilterItems>
                )}
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown"
            ref={competitionNavRef}
        >
            <StatsSection.Main>
                <StatsSection.Nav
                    withScrollableFilter={!showResponsiveItems}
                    useRefinedFilter
                >
                    {showResponsiveItems ? renderMobile : renderDesktop}
                </StatsSection.Nav>
            </StatsSection.Main>
        </div>
    );
};

FixturesNav.propTypes = {
    competitionId: PropTypes.string,
    competitionProviderId: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    ecalId: PropTypes.string.isRequired,
    title: PropTypes.string
};
